import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  NavMenuComponent,
  IconComponent,
  SpinnerComponent,
} from '@navstation-portal/shared/ui/components';
import { IconNames } from '@navstation-portal/shared/data-model/ui';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DrawerModule } from 'primeng/drawer';
import { injectDispatch } from '@ngrx/signals/events';
import { FormsModule } from '@angular/forms';
import { DividerModule } from 'primeng/divider';
import { AppStore } from '@navstation-portal/reporting/data-access/app';
import { reportEvents } from '@navstation-portal/reporting/data-access/vessel-report';
import {
  OffshoreReportStore,
  OffshoreOperationalFormStore,
  OffshorePositionAndWeatherFormStore,
  OffshoreStockFormStore,
  offshoreReportEvents,
  OffshoreConsumptionsFormStore,
} from '@navstation-portal/reporting/data-access/offshore-report';
import { OffshoreReportsStore } from '@navstation-portal/reporting/data-access/offshore-reports';
import { PortsStore } from '@navstation-portal/ports/data-access/store';
import { OffshoreOperationalComponent } from './offshore-operational/offshore-operational.component';
import { OffshoreWeatherComponent } from './offshore-weather/offshore-weather.component';
import { OffshoreStockComponent } from './offshore-stock/offshore-stock.component';
import { OffshoreConsumptionsComponent } from './offshore-consumptions/offshore-consumptions.component';
import { DrawerContentComponent } from './shared/drawer-content/drawer-content.component';
import { FormActionsFooterComponent } from './shared/form-actions-footer/form-actions-footer.component';
import { OffshoreBunkerComponent } from './offshore-bunker/offshore-bunker.component';

@Component({
  selector: 're-offshore-reporting',
  standalone: true,
  templateUrl: './offshore-reporting.component.html',
  styleUrl: './offshore-reporting.component.css',
  imports: [
    NgClass,
    NavMenuComponent,
    ButtonModule,
    CardModule,
    DrawerModule,
    DividerModule,
    FormActionsFooterComponent,
    OffshoreOperationalComponent,
    OffshoreWeatherComponent,
    OffshoreStockComponent,
    OffshoreConsumptionsComponent,
    FormsModule,
    DrawerContentComponent,
    OffshoreBunkerComponent,
    IconComponent,
    SpinnerComponent,
  ],
})
export class OffshoreReportingComponent implements OnInit {
  IconNames = IconNames;

  private route = inject(ActivatedRoute);
  readonly appStore = inject(AppStore);
  readonly portsStore = inject(PortsStore);
  readonly offshoreReportsStore = inject(OffshoreReportsStore);
  readonly offshoreReportStore = inject(OffshoreReportStore);
  readonly operationalStore = inject(OffshoreOperationalFormStore);
  readonly consumptionsStore = inject(OffshoreConsumptionsFormStore);
  readonly positionAndWeatherStore = inject(
    OffshorePositionAndWeatherFormStore,
  );
  readonly stockStore = inject(OffshoreStockFormStore);
  readonly reportDispatch = injectDispatch(reportEvents);
  readonly offshoreReportDispatch = injectDispatch(offshoreReportEvents);

  vesselId: string | null = this.route.snapshot.paramMap.get('vesselId');
  reportId: string | null = this.route.snapshot.paramMap.get('reportId');
  private fetchReportWhenReady = signal(false);
  private hasFetchedExistingReport = false;

  showDrawer = true;

  constructor() {
    effect(() => {
      if (!this.fetchReportWhenReady() || this.hasFetchedExistingReport) {
        return;
      }

      if (this.portsStore.ports().length === 0 || !this.reportId) {
        return;
      }

      this.hasFetchedExistingReport = true;
      this.offshoreReportDispatch.fetchReport(this.reportId);
    });
  }

  ngOnInit() {
    if (!this.vesselId) return;

    this.reportDispatch.getFormFieldRules(this.vesselId);

    const isNewReport = this.route.snapshot.url[0].path === 'new-report';
    if (isNewReport) {
      this.offshoreReportDispatch.createReport({
        vesselId: this.vesselId,
        nextReportNumber: this.offshoreReportsStore.vm().nextReportNumber,
      });
      return;
    }

    if (!this.reportId) return;

    this.fetchReportWhenReady.set(true);
  }

  private activePingEl: HTMLSpanElement | null = null;
  private activePingTimeout: ReturnType<typeof setTimeout> | null = null;

  private resolvePingHost(element: HTMLElement): HTMLElement {
    const isNativeControl =
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement;

    if (isNativeControl) {
      return element.parentElement ?? element;
    }

    const pingHost = element.querySelector<HTMLElement>('[data-ping-host]');
    if (pingHost) {
      return pingHost;
    }

    const innerControl = element.querySelector(
      'input, textarea, select',
    ) as HTMLElement | null;
    if (innerControl) {
      return innerControl.parentElement ?? innerControl;
    }

    return element;
  }

  getStatusForField(fieldId: string): 'valid' | 'invalid' | 'pending' {
    const status = this.offshoreReportStore.vm().validityMap?.[fieldId];
    return status ?? 'pending';
  }

  isStockTable(fieldId: string): boolean {
    return fieldId === 'tanksInUseStock' || fieldId === 'tanksNotInUseStock';
  }

  navigateTo(fieldId: string) {
    const element = document.getElementById(fieldId) as HTMLElement | null;
    if (!element) return;
    const pingHost = this.resolvePingHost(element);

    const scrollContainer = document.getElementById('main-scroll-container');
    const elementPos = pingHost.getBoundingClientRect().top;
    const containerTop = scrollContainer?.getBoundingClientRect().top ?? 0;
    const offSetPos =
      (scrollContainer?.scrollTop ?? 0) + elementPos - containerTop - 250;
    scrollContainer?.scrollTo({
      top: offSetPos,
      behavior: 'smooth',
    });

    const status = this.getStatusForField(fieldId);
    if (status === 'valid') return;

    if (this.activePingTimeout) {
      clearTimeout(this.activePingTimeout);
      this.activePingTimeout = null;
    }
    if (this.activePingEl) {
      this.activePingEl.remove();
      this.activePingEl = null;
    }

    const ping = document.createElement('span');
    ping.className =
      'absolute inset-0 animate-wiggle-soft z-50 border-3 border-white-600 rounded-l-lg rounded-r-none pointer-events-none';

    pingHost.style.position = 'relative';
    pingHost.appendChild(ping);

    this.activePingEl = ping;
    this.activePingTimeout = setTimeout(() => {
      ping.remove();
      if (this.activePingEl === ping) {
        this.activePingEl = null;
      }
      this.activePingTimeout = null;
    }, 1500);
  }
}
